import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../Utils/Axios";
import { resolveMediaUrl } from "../Utils/media";

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

const monthName = (dateString) =>
  new Date(dateString).toLocaleDateString("en-US", { month: "long" });
const dayNumber = (dateString) => new Date(dateString).getDate() || 1;

const fallbackSidebarImages = [
  "/assets/images/blog/blog-sb-1.png",
  "/assets/images/blog/blog-sb-2.png",
  "/assets/images/blog/blog-sb-3.png",
];

const BlogsDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBlog = async () => {
      try {
        let targetSlug = slug;
        if (!targetSlug) {
          const listRes = await api.get("/api/blogs");
          const firstBlog = listRes.data?.blogs?.[0];
          if (!firstBlog?.slug) {
            setLoading(false);
            return;
          }
          targetSlug = firstBlog.slug;
          navigate(`/blog-details/${targetSlug}`, { replace: true });
        }

        const res = await api.get(`/api/blogs/${targetSlug}`);
        setBlog(res.data?.blog || null);
        setRecentBlogs(res.data?.recentBlogs || []);
        setRelatedBlogs(res.data?.relatedBlogs || []);
        setCategories(res.data?.categories || []);
      } catch (err) {
        setBlog(null);
      } finally {
        setLoading(false);
      }
    };
    loadBlog();
  }, [slug, navigate]);

  const contentBlocks = useMemo(() => {
    if (!blog?.content) return [];
    return String(blog.content)
      .split(/\n{2,}/)
      .map((item) => item.trim())
      .filter(Boolean);
  }, [blog]);

  if (loading) {
    return (
      <div className="container pt-120 mb-120">
        <p>Loading blog details...</p>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="container pt-120 mb-120">
        <p>Blog not found.</p>
      </div>
    );
  }

  const coverImage = resolveMediaUrl(blog.coverImage) || "/assets/images/blog/blog-dt-img.png";

  return (
    <div>
      <div className="inner-banner">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="banner-content text-center">
                <h1>Blog</h1>
                <span />
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Home</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/blog">Blog</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Details
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="blog-details-area pt-120 mb-120">
        <div className="container">
          <div className="row g-lg-4 gy-5 justify-content-center">
            <div className="col-lg-8">
              <div className="blog-details-wrap">
                <div className="recent-article-img">
                  <img className="img-fluid" src={coverImage} alt={blog.title} />
                  <div className="publish-area d-xl-none d-flex">
                    <Link to={`/blog-details/${blog.slug}`}>
                      <span>{String(dayNumber(blog.publishedAt)).padStart(2, "0")}</span>
                      {monthName(blog.publishedAt)}
                    </Link>
                  </div>
                </div>
                <div className="recent-article-content">
                  <div className="recent-article-meta">
                    <div className="publish-area">
                      <Link to={`/blog-details/${blog.slug}`}>
                        <span>{String(dayNumber(blog.publishedAt)).padStart(2, "0")}</span>
                        {monthName(blog.publishedAt)}
                      </Link>
                    </div>
                    <ul>
                      <li>
                        <span>
                          <img src="/assets/images/icon/comment.svg" alt="" />
                          {blog.commentsCount || 0} Comments
                        </span>
                      </li>
                      <li>
                        <span>
                          <img src="/assets/images/icon/user.svg" alt="" />
                          {blog.authorName || "System Admin"}
                        </span>
                      </li>
                    </ul>
                  </div>
                  <h2>{blog.title}</h2>
                  <div className="for-devider" />
                  {contentBlocks.length === 0 ? (
                    <p>{blog.excerpt}</p>
                  ) : (
                    contentBlocks.map((block, idx) => <p key={`${idx}-${block.slice(0, 12)}`}>{block}</p>)
                  )}

                  <div className="blog-tag-social-area">
                    <div className="bolg-tag">
                      <h6>Tag:</h6>
                      <ul>
                        {(blog.tags || []).length === 0 ? (
                          <li>
                            <span>General</span>
                          </li>
                        ) : (
                          blog.tags.map((tag) => (
                            <li key={tag}>
                              <Link to={`/blog?tag=${encodeURIComponent(tag)}`}>{tag},</Link>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                    <div className="social-area">
                      <h6>Published:</h6>
                      <ul className="social-link d-flex align-items-center">
                        <li>
                          <span>{formatDate(blog.publishedAt)}</span>
                        </li>
                        <li>
                          <span>{blog.readTimeMinutes || 1} min read</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {relatedBlogs.length > 0 ? (
                <div className="next-prev-pages-area mb-120">
                  {relatedBlogs.slice(0, 2).map((item, index) => (
                    <div className={`previous-area next-area ${index === 1 ? "two" : ""}`} key={item._id}>
                      <Link to={`/blog-details/${item.slug}`}>
                        <div className="btn-wrap">
                          <img src={resolveMediaUrl(item.coverImage) || coverImage} alt={item.title} />
                          <span>{index === 0 ? "Related Post" : "Next Read"}</span>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="col-lg-4">
              <div className="job-sidebar">
                <div className="job-widget style-1 mb-20">
                  <div className="check-box-item">
                    <h5 className="job-widget-title">Category</h5>
                    <div className="checkbox-container">
                      <ul>
                        {categories.length === 0 ? (
                          <li>
                            <span className="text">No categories</span>
                          </li>
                        ) : (
                          categories.map((item) => (
                            <li key={item.name}>
                              <label className="containerss">
                                <input type="checkbox" checked={item.name === blog.category} readOnly />
                                <span className="checkmark" />
                                <span className="text">{item.name}</span>
                                <span className="qty">({item.count})</span>
                              </label>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="job-widget mb-20">
                  <h5 className="job-widget-title mb-10">Recent Post</h5>
                  <ul className="recent-activitys">
                    {recentBlogs.length === 0 ? (
                      <li>
                        <div className="content">
                          <h6>No recent posts</h6>
                        </div>
                      </li>
                    ) : (
                      recentBlogs.map((item, index) => (
                        <li key={item._id}>
                          <div className="blog-img">
                            <img
                            src={
                              resolveMediaUrl(item.coverImage) ||
                              fallbackSidebarImages[index % fallbackSidebarImages.length]
                            }
                              alt={item.title}
                            />
                          </div>
                          <div className="content">
                            <h6>
                              <Link to={`/blog-details/${item.slug}`}>{item.title}</Link>
                            </h6>
                            <span>
                              <img src="/assets/images/icon/calender2.svg" alt="" />{" "}
                              {formatDate(item.publishedAt)}
                            </span>
                          </div>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
                <div className="job-widget">
                  <div className="check-box-item">
                    <h5 className="job-widget-title mb-10">Blog Tags</h5>
                    <ul className="tags">
                      {(blog.tags || []).length === 0 ? (
                        <li>
                          <span>General</span>
                        </li>
                      ) : (
                        blog.tags.map((tag) => (
                          <li key={tag}>
                            <Link to={`/blog?tag=${encodeURIComponent(tag)}`}>{tag},</Link>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                </div>
              </div>
              <div className="job-card">
                <div className="job-content">
                  <h5>Explore More Healthcare Jobs</h5>
                  <p>Find live opportunities that match your category, city and ZIP code.</p>
                  <div className="find-job-btn">
                    <Link className="primry-btn-2 lg-btn " to="/find-job">
                      Find Job
                    </Link>
                  </div>
                </div>
                <div className="job-img">
                  <img src="/assets/images/blog/find-job.png" alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogsDetail;
